import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  NgbDateAdapter,
  NgbDateNativeUTCAdapter,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbModal
} from '@ng-bootstrap/ng-bootstrap';
import { createMask } from '@ngneat/input-mask';
import { saveAs } from 'file-saver';
import { catchError, concatMap, defer, forkJoin, from, map, of } from 'rxjs';
import { CompanyVendorFileForm } from 'src/app/classes/form-groups/company-vendor-file-form';
import { InsuranceCertificateForm } from 'src/app/classes/form-groups/insurance-certificate-form';
import { InsurancePolicyForm } from 'src/app/classes/form-groups/insurance-policy-form';
import { InsurancePolicySiteForm } from 'src/app/classes/form-groups/insurance-policy-site-form';
import { SiteForm } from 'src/app/classes/form-groups/site-form';
import {
  Company,
  CompanyVendorFile,
  File as FileModel,
  InsuranceCertificate,
  License,
  SubNationalDivision,
  Vendor
} from 'src/app/classes/models';
import { ComplianceSetting } from 'src/app/classes/models/compliance-setting';
import { InsuranceAgent } from 'src/app/classes/models/insurance-agent';
import { CustomNgbDateFormatter } from 'src/app/dependencies/custom-ngb-date-formatter';
import { AmBestRating } from 'src/app/interfaces/am-best-rating';
import { COIStatuses } from 'src/app/interfaces/coi-statuses';
import { FinancialSize } from 'src/app/interfaces/financial-size';
import { InsuranceCertificateStatus } from 'src/app/interfaces/insurance-certificate-status';
import { InsurancePolicyTypeAbbreviated } from 'src/app/interfaces/insurance-policy-type-abbreviation';
import { Insurer } from 'src/app/interfaces/insurer';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { InsurancePolicySitesModalComponent } from 'src/app/modals/insurance-policy-sites-modal/insurance-policy-sites-modal.component';
import { AdminService } from 'src/app/services/admin.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ComplianceService } from 'src/app/services/compliance.service';
import { FileService } from 'src/app/services/file.service';
import { InsuranceService } from 'src/app/services/insurance.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

declare type InsurancePolicyType =
  | 'General Liability'
  | 'Automotive Liability'
  | 'Umbrella Liability'
  | "Worker's Compensation"
  | 'Professional Liability';

@Component({
  selector: 'app-add-edit-coi',
  templateUrl: './add-edit-coi.component.html',
  styleUrls: ['./add-edit-coi.component.less'],
  providers: [
    // A COI day arrives as UTC midnight. Local calendar parts show the previous day in US timezones.
    { provide: NgbDateAdapter, useClass: NgbDateNativeUTCAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomNgbDateFormatter }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditCOIComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private InsuranceService: InsuranceService,
    private CompanyVendorService: CompanyVendorService,
    private ComplianceService: ComplianceService,
    private UtilityService: UtilityService,
    private SubNationalDivisionService: SubNationalDivisionService,
    private AdminService: AdminService,
    private ModalService: NgbModal,
    private FileService: FileService,
    private cdr: ChangeDetectorRef
  ) {}

  ReplaceCOIId?: number;

  CompanyVendorId!: number;
  CompanyId!: number;
  VendorId!: number;
  CompanyComplianceTypeId!: number;
  InsuranceAgentId!: number;
  action!: 'review' | 'edit';
  vendorRiskLevel!: number;

  Vendor!: Vendor;
  Company!: Company;
  InsuranceAgent!: InsuranceAgent;
  ComplianceSetting!: ComplianceSetting;
  // Separate the primary COI file from the supporting files. When loading file data,
  // split into these two arrays, then merge into one and apply to Insurance Certificate form group
  // when saving happens
  COIFiles: FileModel[] = [];
  SupportingFiles: FileModel[] = [];
  SubNationalDivisions: SubNationalDivision[] = [];

  minDatePickerDate: NgbDateStruct = { day: 1, month: 1, year: 1900 };

  InsuranceCertificateFormGroup = new InsuranceCertificateForm();
  country!: 'US' | 'CA';

  InsuranceCertificateStatuses: COIStatuses[] = [
    'Received',
    'Reviewing',
    'Accepted',
    'Rejected',
    'Archived',
    'Replaced',
    'Draft'
  ].sort((a, b) => (a < b ? -1 : 1)) as COIStatuses[];

  updateAvailableStatuses() {
    const currentStatus =
      this.InsuranceCertificateFormGroup.get('status')?.value;
    const baseStatuses: COIStatuses[] = [
      'Received',
      'Reviewing',
      'Accepted',
      'Rejected',
      'Archived',
      'Replaced'
    ];

    const conditionalStatuses: COIStatuses[] = [];
    if (currentStatus === 'Draft') {
      conditionalStatuses.push('Draft' as COIStatuses);
    }
    if (currentStatus === 'Created') {
      conditionalStatuses.push('Created' as COIStatuses);
    }

    this.InsuranceCertificateStatuses = [
      ...baseStatuses,
      ...conditionalStatuses
    ].sort((a, b) => (a < b ? -1 : 1)) as COIStatuses[];
  }

  Insurers: Map<string, Insurer> = new Map([
    [
      'A',
      {
        letter: 'A',
        naic: null,
        name: null,
        amBestNumber: null,
        amBestRating: null,
        sizeCategory: null
      }
    ],
    [
      'B',
      {
        letter: 'B',
        naic: null,
        name: null,
        amBestNumber: null,
        amBestRating: null,
        sizeCategory: null
      }
    ],
    [
      'C',
      {
        letter: 'C',
        naic: null,
        name: null,
        amBestNumber: null,
        amBestRating: null,
        sizeCategory: null
      }
    ],
    [
      'D',
      {
        letter: 'D',
        naic: null,
        name: null,
        amBestNumber: null,
        amBestRating: null,
        sizeCategory: null
      }
    ],
    [
      'E',
      {
        letter: 'E',
        naic: null,
        name: null,
        amBestNumber: null,
        amBestRating: null,
        sizeCategory: null
      }
    ],
    [
      'F',
      {
        letter: 'F',
        naic: null,
        name: null,
        amBestNumber: null,
        amBestRating: null,
        sizeCategory: null
      }
    ]
  ]);

  // NEED TO FIX CHANGING INSURER ALREADY ASSIGNED TO POLICY
  // NEED TO FIX UPDATING AM Best Rating & Financial Size Category.
  InsurersMappedToInsurancePolicyFormGroups = new Map<
    Insurer,
    InsurancePolicyForm[]
  >();

  AvailableInsurers: Insurer[] = [
    // Empty option
    // { letter: '', naic: null, name: null, amBestNumber: null, amBestRating: null, sizeCategory: null }
  ];

  AvailableInsurersMappedByLetter = new Map<string, Insurer>([]);

  PolicyTypeToLabelMap = new Map<InsurancePolicyTypeAbbreviated, string>([
    ['GL', 'General Liability'],
    ['AUTO', 'Automotive Liability'],
    ['UMB', 'Umbrella Liability'],
    ['WC', "Worker's Compensation & Employer's Liability"],
    ['PL', 'Professional Liability / Errors & Omissions']
  ]);

  AMBestRatings = new Map<string, string>([
    ['A++', 'A++'],
    ['A+', 'A+'],
    ['A', 'A'],
    ['A-', 'A-'],
    ['B++', 'B++'],
    ['B+', 'B+'],
    ['B', 'B'],
    ['B-', 'B-'],
    ['C++', 'C++'],
    ['C+', 'C+'],
    ['C', 'C'],
    ['C-', 'C-'],
    ['D', 'D'],
    ['E', 'E'],
    ['F', 'F'],
    ['S', 'S'],
    ['NR', 'Not Rated']
  ]);

  SizeCategories = [
    'I',
    'II',
    'III',
    'IV',
    'V',
    'VI',
    'VII',
    'VIII',
    'IX',
    'X',
    'XI',
    'XII',
    'XIII',
    'XIV',
    'XV'
  ];

  numberMask = createMask({ regex: '\\d*' });
  alphanumericMask = createMask({ regex: '[\\w-]*' });
  currencyMask = createMask({
    alias: 'numeric',
    groupSeparator: ',',
    placeholder: '',
    rightAlign: false
    // parser: (value: string) => {
    //   return parseInt(value.replace(/[^\d]/g, ''), 10);
    // }
  });

  // The currency mask parser doesn't work, so the model is parsed here.
  // The mask owns the input text. Writing the parsed number back into it
  // on blur can clear the field.
  applyCurrencyFormat(control: FormControl, value: unknown): void {
    const digits =
      value === null || value === undefined
        ? ''
        : String(value).replace(/[^\d]/g, '');
    const parsed = digits.length === 0 ? null : parseInt(digits, 10);

    control.patchValue(parsed, {
      emitModelToViewChange: false,
      emitViewToModelChange: false
    });
  }

  SelectedInsurancePolicyType?: InsurancePolicyTypeAbbreviated;
  SelectedInsurancePolicyTypes: InsurancePolicyTypeAbbreviated[] = [];
  preventInput = this.UtilityService.preventInput;

  PolicyTypeToPolicies: {
    GL: 'General Liability';
    AUTO: 'Automotive Liability';
    UMB: 'Umbrella Liability';
    WC: "Worker's Compensation";
    PL: 'Professional Liability';
  } = {
    GL: 'General Liability',
    AUTO: 'Automotive Liability',
    UMB: 'Umbrella Liability',
    WC: "Worker's Compensation",
    PL: 'Professional Liability'
  };

  AvailableInsurancePoliciesMap = new Map<
    InsurancePolicyTypeAbbreviated | null,
    InsurancePolicyType | ''
  >([
    ['GL', 'General Liability'],
    ['AUTO', 'Automotive Liability'],
    ['UMB', 'Umbrella Liability'],
    ['WC', "Worker's Compensation"],
    ['PL', 'Professional Liability']
  ]);

  AvailableInsurancePolicies: [
    InsurancePolicyTypeAbbreviated | null,
    InsurancePolicyType | ''
  ][] = [];

  insurancePolicyFormSort(InsurancePolicyFormGroups: InsurancePolicyForm[]) {
    const PolicyTypeToIndexMap = {
      GL: 0,
      AUTO: 1,
      UMB: 2,
      WC: 3,
      PL: 4
    };

    return InsurancePolicyFormGroups.sort((a, b) => {
      const {
        value: { policyType: APolicyType }
      } = a;
      const {
        value: { policyType: BPolicyType }
      } = b;
      if (APolicyType && BPolicyType) {
        return (
          PolicyTypeToIndexMap[APolicyType] - PolicyTypeToIndexMap[BPolicyType]
        );
      }

      return 0;
    });
  }

  getFilesFromCompanyVendorFiles(CompanyVendorFiles: CompanyVendorFile[]) {
    return CompanyVendorFiles.map((CompanyVendorFile) => {
      const { File } = CompanyVendorFile;

      if (!File) {
        throw new Error('Client/Vendor file is missing file data.');
      }

      return File;
    });
  }

  onPolicySelection(option: InsurancePolicyTypeAbbreviated) {
    const index = this.SelectedInsurancePolicyTypes.indexOf(option);

    index > -1
      ? this.SelectedInsurancePolicyTypes.splice(index, 1)
      : this.SelectedInsurancePolicyTypes.push(option);
  }

  deselectOption(option: InsurancePolicyTypeAbbreviated) {
    const index = this.SelectedInsurancePolicyTypes.findIndex(
      (type) => type === option
    );

    this.SelectedInsurancePolicyTypes.splice(index, 1);
    this.addAvailableInsurancePolicy(option);
  }

  selectOption(option: InsurancePolicyTypeAbbreviated) {
    if (option) {
      this.SelectedInsurancePolicyTypes.push(option);

      this.removeAvailableInsurancePolicy(option);
    }
  }

  selectPolicySites(InsurancePolicyFormGroup: InsurancePolicyForm) {
    const InsurancePolicySitesModal = this.ModalService.open(
      InsurancePolicySitesModalComponent,
      { backdrop: 'static', size: 'lg' }
    );
    const { componentInstance } = InsurancePolicySitesModal;
    Object.assign(componentInstance, {
      CompanyId: this.CompanyId,
      InsurancePolicyFormGroup
    });
  }

  addAvailableInsurancePolicy(policyType: InsurancePolicyTypeAbbreviated) {
    const policy = this.PolicyTypeToPolicies[policyType];

    this.AvailableInsurancePoliciesMap.set(policyType, policy);
    this.AvailableInsurancePolicies = Array.from(
      this.AvailableInsurancePoliciesMap
    );
  }

  removeAvailableInsurancePolicy(policyType: InsurancePolicyTypeAbbreviated) {
    this.AvailableInsurancePoliciesMap.delete(policyType);
    this.AvailableInsurancePolicies = Array.from(
      this.AvailableInsurancePoliciesMap
    );
  }

  addInsurancePolicies() {
    if (this.SelectedInsurancePolicyTypes.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You must select a Insurance Policy to add.',
        { returnError: true }
      );
    }

    this.SelectedInsurancePolicyTypes.forEach((type) =>
      this.addInsurancePolicy(type)
    );

    this.SelectedInsurancePolicyTypes = [];
  }

  addInsurancePolicy(policyType?: InsurancePolicyTypeAbbreviated) {
    if (policyType) {
      this.SelectedInsurancePolicyType = policyType;
    }

    if (!this.SelectedInsurancePolicyType) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You must select an Insurance Policy to add.',
        { returnError: true }
      );
    }

    const InsurancePolicyFormGroup = new InsurancePolicyForm();
    InsurancePolicyFormGroup.setControl(
      'InsurancePolicySites',
      new FormArray([])
    );
    InsurancePolicyFormGroup.patchValue({
      policyType: this.SelectedInsurancePolicyType,
      status: 'Active'
    });

    // Correctly map policies when added.
    const PolicyTypeToIndexMap = {
      GL: 0,
      AUTO: 1,
      UMB: 2,
      WC: 3,
      PL: 4
    };

    this.InsuranceCertificateFormGroup.controls['InsurancePolicies'].insert(
      PolicyTypeToIndexMap[this.SelectedInsurancePolicyType],
      InsurancePolicyFormGroup
    );
    this.setPolicyFormControlRequirements();

    if (this.SelectedInsurancePolicyType === 'AUTO') {
      const { value: isCaSplitLimit } =
        InsurancePolicyFormGroup.controls['isCaSplitLimit'];
      this.splitLimitPolicyChange(!!isCaSplitLimit);
    }

    this.removeAvailableInsurancePolicy(this.SelectedInsurancePolicyType);
  }

  isPolicyRequiredByClient(InsurancePolicyFormGroup: InsurancePolicyForm) {
    const { hasProfessionalTrade, hasProfessionalOrNonProfessionalTrade } =
      this.Vendor;
    const {
      policyType: { value: policyType }
    } = InsurancePolicyFormGroup.controls;

    if (!policyType) {
      throw new Error('Missing policy type.');
    }

    const PolicyTypeToComplianceSettingRequirement: {
      GL: 'generalLiability_isRequired';
      AUTO: 'autoLiability_isRequired';
      UMB: 'umbrellaLiability_isRequired';
      WC: 'workersCompensation_isRequired';
      PL: 'errorsAndOmissions_isRequired';
    } = {
      GL: 'generalLiability_isRequired',
      AUTO: 'autoLiability_isRequired',
      UMB: 'umbrellaLiability_isRequired',
      WC: 'workersCompensation_isRequired',
      PL: 'errorsAndOmissions_isRequired'
    };

    if (policyType === 'PL') {
      return (
        this.ComplianceSetting[
          PolicyTypeToComplianceSettingRequirement[policyType]
        ] &&
        (hasProfessionalTrade || hasProfessionalOrNonProfessionalTrade)
      );
    }

    return this.ComplianceSetting[
      PolicyTypeToComplianceSettingRequirement[policyType]
    ];
  }

  isPolicyRequiredAndManagedByAgent(
    InsurancePolicyFormGroup: InsurancePolicyForm
  ) {
    const {
      policyType: { value: policyType }
    } = InsurancePolicyFormGroup.controls;

    if (!policyType) {
      throw new Error('Missing policy type');
    }

    const PolicyTypeToInsuranceAgentProvider: {
      GL: 'isGLProvider';
      AUTO: 'isAutoProvider';
      UMB: 'isUmbrellaProvider';
      WC: 'isWorkersProvider';
      PL: 'isProfessionalProvider';
    } = {
      GL: 'isGLProvider',
      AUTO: 'isAutoProvider',
      UMB: 'isUmbrellaProvider',
      WC: 'isWorkersProvider',
      PL: 'isProfessionalProvider'
    };

    return (
      this.isPolicyRequiredByClient(InsurancePolicyFormGroup) &&
      this.InsuranceAgent[PolicyTypeToInsuranceAgentProvider[policyType]]
    );
  }

  setPolicyFormControlRequirements() {
    const PolicyTypeToControls: {
      GL: ['perOccurence', 'aggregate'];
      AUTO: [];
      UMB: ['perOccurence'];
      WC: ['elEachAccident', 'elDiseaseEachEmployee', 'elDiseasePerPolicy'];
      PL: ['perOccurence'];
    } = {
      GL: ['perOccurence', 'aggregate'],
      AUTO: [],
      UMB: ['perOccurence'],
      WC: ['elEachAccident', 'elDiseaseEachEmployee', 'elDiseasePerPolicy'],
      PL: ['perOccurence']
    };

    this.InsuranceCertificateFormGroup.controls[
      'InsurancePolicies'
    ].controls.forEach((InsurancePolicyFormGroup) => {
      const {
        policyType: { value: policyType },
        isCaSplitLimit: { value: isCaSplitLimit }
      } = InsurancePolicyFormGroup.controls;

      if (!policyType) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Insurance policy is missing policy type.',
          { returnError: true }
        );
      }

      // If the policy is required and the agent manages the policy, we'll mark the form controls as required.
      if (this.isPolicyRequiredAndManagedByAgent(InsurancePolicyFormGroup)) {
        InsurancePolicyFormGroup.controls['insurer'].setValidators(
          Validators.required
        );
        InsurancePolicyFormGroup.controls['insurerLetter'].setValidators(
          Validators.required
        );
        // Backend also sets this value.
        InsurancePolicyFormGroup.patchValue({ isRequired: true });

        PolicyTypeToControls[policyType].forEach((controlName) => {
          InsurancePolicyFormGroup.controls[controlName].setValidators(
            Validators.required
          );
        });
      }

      if (policyType === 'AUTO') {
        this.splitLimitPolicyChange(isCaSplitLimit);
      }
    });
  }

  downloadFile(File: FileModel | License) {
    if (!(File instanceof FileModel)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error downloading the file. File is not an instance of File model.',
        { returnError: true }
      );
    }

    const { id: FileId } = File;

    if (!FileId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error downloading the file. File is missing Id.',
        { returnError: true }
      );
    }

    this.CompanyVendorService.getCompanyVendor(
      this.UtilityService.stringifyObjectsInQueryObject({
        attributes: ['id'],
        CompanyVendorFile: {
          options: { required: true, duplicating: false },
          FileId,
          File: {
            options: { required: true }
          }
        }
      })
    ).subscribe({
      next: (CompanyVendor) => {
        const { CompanyVendorFiles } = CompanyVendor;

        if (!CompanyVendorFiles) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client/Vendor Insurance Certificate file.',
            { returnError: true }
          );
        }

        if (
          !Array.isArray(CompanyVendorFiles) ||
          CompanyVendorFiles.length !== 1
        ) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Client/Vendor Insurance Certificate file was not loaded properly.',
            { returnError: true }
          );
        }

        const CompanyVendorFile = CompanyVendorFiles[0];

        const { id: CompanyVendorFileId, File } = CompanyVendorFile;

        if (!CompanyVendorFileId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing CompanyVendorFileId.',
            { returnError: true }
          );
        }

        if (!File) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing File information.',
            { returnError: true }
          );
        }

        const { filetype, filename, filepath, FileContent } = File;

        if (filepath) {
          this.FileService.getCompanyVendorFileS3PresignedUrl({
            id: CompanyVendorFileId
          }).subscribe({
            next: ({ presignedUrl, filename }) => {
              saveAs(presignedUrl, filename);
            }
          });
        } else {
          if (!filename) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing file name.',
              {
                returnError: true
              }
            );
          }

          if (!FileContent) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing File data.',
              {
                returnError: true
              }
            );
          }

          const { filecontents } = FileContent;

          saveAs(`data:${filetype};base64,${filecontents}`, filename);
        }
      }
    });
  }

  AMBestLookup() {
    window.open('http://ratings.ambest.com', '_blank');
  }

  // Need to change this to archive policy.
  cancelPolicy(
    InsurancePolicyFormGroup: InsurancePolicyForm,
    PolicyFormGroupIndex: number
  ) {
    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', modalDialogClass: 'confirm-modal' }
    );
    Object.assign(componentInstance, {
      displayText: 'Are you sure you want to cancel this policy?'
    });
    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          const { value: policyType } =
            InsurancePolicyFormGroup.controls['policyType'];

          if (!policyType) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Insurance Policy form is missing policy type.',
              { returnError: true }
            );
          }

          InsurancePolicyFormGroup.patchValue({
            status: 'Cancelled',
            isArchived: true
          });

          const { value: InsurancePolicyRecord } = InsurancePolicyFormGroup;
          const { id: InsurancePolicyId } = InsurancePolicyRecord;

          (() => {
            if (InsurancePolicyId) {
              return this.InsuranceService.saveInsurancePolicy(
                InsurancePolicyRecord
              ).pipe(
                catchError((error) => {
                  InsurancePolicyFormGroup.patchValue({
                    status: undefined,
                    isArchived: false
                  });
                  return of(error);
                })
              );
            }
            return of([]);
          })().subscribe({
            next: () => {
              this.InsuranceCertificateFormGroup.controls[
                'InsurancePolicies'
              ].removeAt(PolicyFormGroupIndex);

              const PolicyTypeToIndexMap = {
                GL: 0,
                AUTO: 1,
                UMB: 2,
                WC: 3,
                PL: 4
              };

              this.AvailableInsurancePoliciesMap.set(
                policyType,
                this.PolicyTypeToPolicies[policyType]
              );
              // Reorder the selections appropriately.
              this.AvailableInsurancePolicies = Array.from(
                this.AvailableInsurancePoliciesMap.entries()
              ).sort((a, b) => {
                if (a[0] !== null && b[0] !== null) {
                  return (
                    PolicyTypeToIndexMap[a[0]] - PolicyTypeToIndexMap[b[0]]
                  );
                }
                return 0;
              });
            }
          });
        }
      }
    });
  }

  splitLimitPolicyChange(event: boolean) {
    const AutoInsurancePolicyFormGroup =
      this.InsuranceCertificateFormGroup.controls[
        'InsurancePolicies'
      ].controls.find(
        (InsurancePolicyFormGroup) =>
          InsurancePolicyFormGroup.controls['policyType'].value === 'AUTO'
      );

    if (!AutoInsurancePolicyFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Auto Insurance Policy.',
        { returnError: true }
      );
    }

    const {
      combinedSingle: combinedSingleControl,
      biPerPerson: biPerPersonControl,
      biPerAccident: biPerAccidentControl,
      propertyDamage: propertyDamageControl
    } = AutoInsurancePolicyFormGroup.controls;

    if (event) {
      biPerPersonControl.setValidators(Validators.required);
      biPerAccidentControl.setValidators(Validators.required);
      propertyDamageControl.setValidators(Validators.required);
      combinedSingleControl.patchValue(null);
      combinedSingleControl.removeValidators(Validators.required);
    } else {
      combinedSingleControl.setValidators(Validators.required);
      biPerPersonControl.patchValue(null);
      biPerAccidentControl.patchValue(null);
      propertyDamageControl.patchValue(null);
      biPerPersonControl.removeValidators(Validators.required);
      biPerAccidentControl.removeValidators(Validators.required);
      propertyDamageControl.removeValidators(Validators.required);
    }

    AutoInsurancePolicyFormGroup.controls['combinedSingle'][
      event ? 'disable' : 'enable'
    ]();
    AutoInsurancePolicyFormGroup.controls['biPerAccident'][
      !event ? 'disable' : 'enable'
    ]();
    AutoInsurancePolicyFormGroup.controls['biPerPerson'][
      !event ? 'disable' : 'enable'
    ]();
    AutoInsurancePolicyFormGroup.controls['propertyDamage'][
      !event ? 'disable' : 'enable'
    ]();
  }

  performLookup(InsurerLetter: string, Insurer: Insurer) {
    const { naic, letter /*amBestNumber*/ } = Insurer;

    const AmBestQuery = {};

    if (naic) {
      Object.assign(AmBestQuery, { nAICCompanyNumber: naic });
    }

    this.UtilityService.stringifyObjectsInQueryObject(AmBestQuery);

    if (Object.keys(AmBestQuery).length !== 0) {
      return this.AdminService.getAmBest(AmBestQuery).pipe(
        map((AMBest) => {
          if (AMBest) {
            const {
              aMBestCompanyNumber: amBestNumber,
              nAICCompanyNumber: naic,
              aMBestRatingAlpha: amBestRating,
              financialSizeCategoryAlpha: sizeCategory,
              companyGroupName: name
            } = AMBest;

            if (!naic) {
              throw this.ToastService.addErrorToast(
                'Error',
                'AM Best record is missing NAIC number.',
                { returnError: true }
              );
            }

            Object.assign(Insurer, {
              amBestNumber,
              naic,
              amBestRating: amBestRating
                ? this.AMBestRatings.get(amBestRating)
                : null,
              sizeCategory,
              name
            });

            // If the insurer is replaced by a new insurer, remove the old insurer.
            if (this.Insurers.get(InsurerLetter)) {
              const FoundInsurerIndex = this.AvailableInsurers.findIndex(
                (ExistingInsurer) =>
                  ExistingInsurer === this.Insurers.get(InsurerLetter)
              );
              if (FoundInsurerIndex !== -1) {
                const Insurers = this.AvailableInsurers.splice(
                  FoundInsurerIndex,
                  1
                );
                const RemovedInsurer = Insurers.pop();
                if (RemovedInsurer) {
                  const { letter } = RemovedInsurer;
                  if (letter) {
                    this.AvailableInsurersMappedByLetter.delete(letter);
                  }
                }
              }
            }

            this.AvailableInsurers.push(Insurer);
            this.AvailableInsurersMappedByLetter.set(letter, Insurer);

            const InsurancePolicyFields: (
              | 'aMBestCompanyNumber'
              | 'amBestRating'
              | 'financialSize'
              | 'insurer'
              | 'naic'
            )[] = [
              'aMBestCompanyNumber',
              'amBestRating',
              'financialSize',
              'insurer',
              'naic'
            ];

            const InsurancePolicyToInsurerFieldMap: {
              aMBestCompanyNumber: 'amBestNumber';
              amBestRating: 'letter';
              financialSize: 'sizeCategory';
              insurer: 'name';
              naic: 'naic';
            } = {
              aMBestCompanyNumber: 'amBestNumber',
              amBestRating: 'letter',
              financialSize: 'sizeCategory',
              insurer: 'name',
              naic: 'naic'
            };

            InsurancePolicyFields.forEach((field) => {
              this.insurerInformationChanged(
                Insurer,
                Insurer[InsurancePolicyToInsurerFieldMap[field]],
                field
              );
            });
          }
        })
      );
    }

    return of<void | null>(null);
  }

  // Updates insurer information on form group when changed.
  insurerInformationChanged(
    Insurer: Insurer,
    value: any,
    field:
      | 'aMBestCompanyNumber'
      | 'amBestRating'
      | 'financialSize'
      | 'naic'
      | 'insurer'
  ) {
    const FoundInsurancePolicyFormGroups =
      this.InsurersMappedToInsurancePolicyFormGroups.get(Insurer);

    if (FoundInsurancePolicyFormGroups) {
      FoundInsurancePolicyFormGroups.forEach(
        (FoundInsurancePolicyFormGroup) => {
          FoundInsurancePolicyFormGroup.patchValue({ [field]: value });
        }
      );
    }
  }

  insurerNameChanged(Insurer: Insurer, name: string) {
    const { letter } = Insurer;
    Object.assign(Insurer, { name });

    const FoundInsurerIndex = this.AvailableInsurers.findIndex(
      (InsurerRecord) => InsurerRecord === Insurer
    );
    const FoundInsurancePolicyFormGroups =
      this.InsurersMappedToInsurancePolicyFormGroups.get(Insurer);

    if (!name && FoundInsurerIndex !== -1) {
      this.AvailableInsurers.splice(FoundInsurerIndex, 1);
      this.AvailableInsurersMappedByLetter.delete(letter);
      if (FoundInsurancePolicyFormGroups) {
        FoundInsurancePolicyFormGroups.forEach(
          (FoundInsurancePolicyFormGroup) => {
            // Update the insurance policy to blank out all insurer related information.
            this.setPolicyInsurer(FoundInsurancePolicyFormGroup, '');
            this.InsurersMappedToInsurancePolicyFormGroups.delete(Insurer);
          }
        );
      }
    }

    if (FoundInsurerIndex === -1) {
      this.AvailableInsurers.push(Insurer);
      this.AvailableInsurersMappedByLetter.set(letter, Insurer);
    }

    if (name && FoundInsurancePolicyFormGroups) {
      FoundInsurancePolicyFormGroups.forEach(
        (FoundInsurancePolicyFormGroup) => {
          this.setPolicyInsurer(FoundInsurancePolicyFormGroup, letter);
        }
      );
    }
  }

  // This is the COI file upload, not for supporting documents.
  fileUpload(
    { data, type }: { data: File; type: null | 'No Scan' },
    docType: 'COI' | 'Supporting File'
  ) {
    const { name: filename, size: filesize, type: filetype } = data;

    from(data.arrayBuffer()).subscribe({
      next: (filecontents) => {
        const {
          controls: {
            CompanyVendorId: { value: CompanyVendorId },
            CompanyId: { value: CompanyId },
            VendorId: { value: VendorId }
          }
        } = this.InsuranceCertificateFormGroup;

        const fileDataString =
          this.UtilityService.convertArrayBufferToBase64EncodedString(
            filecontents
          );

        const CompanyVendorFile: CompanyVendorFile = {
          docType,
          CompanyVendorId,
          CompanyId,
          VendorId
        };

        const FileData = {
          filename,
          filesize,
          filetype,
          FileContent: { filecontents: fileDataString }
        };

        Object.assign(CompanyVendorFile, { File: FileData });
        if (docType === 'COI') {
          this.COIFiles.push(CompanyVendorFile);
          this.cdr.markForCheck();

          // This functionallity will now be triggered always, but the scan will be done in the backend

          // if (type !== 'No Scan') {
          //   const { nativeElement } = this.HostElement;
          //   const mask = new LoadingMask(nativeElement, {
          //     maskText: 'Uploading COI...'
          //   });
          //   this.AdminService.scanInsuranceCertificateFile(FileData).subscribe({
          //     next: (scanResults) => {
          //       mask.destroy();

          //       const {
          //         'PRODUCER NAME': prodName,
          //         'PRODUCER ADDRESS': prodAddress,
          //         'PRODUCER CITY': prodCity,
          //         'PRODUCER STATE': prodState,
          //         'PRODUCER ZIP': prodZip,
          //         Contact: contactName,
          //         Phone: contactPhone,
          //         Fax: contactFax,
          //         ADDRESS: contactEmail,

          //         'INSURER A': InsurerAName,
          //         'INSURER A NAIC': InsurerANAIC,
          //         'INSURER B': InsurerBName,
          //         'INSURER B NAIC': InsurerBNAIC,
          //         'INSURER C': InsurerCName,
          //         'INSURER C NAIC': InsurerCNAIC,
          //         'INSURER D': InsurerDName,
          //         'INSURER D NAIC': InsurerDNAIC,
          //         'INSURER E': InsurerEName,
          //         'INSURER E NAIC': InsurerENAIC,
          //         'INSURER F': InsurerFName,
          //         'INSURER F NAIC': InsurerFNAIC,

          //         'CL INS LETTER': GLInsurerLetter,
          //         'CL POLICY NMBER': GLPolicyNumber,
          //         'CL POLICY EFF': GLPolicyEffectiveDate,
          //         'CL POLICY EXP': GLPolicyExpirationDate,
          //         'CL ADDL': GLAddlInsured,
          //         'CL SUBR': GLSubrWvd,
          //         'EACH OCCURRENCE': GLPerOccurrence,
          //         'DAMAGE TO RENTED': GLDamageToRented,
          //         'MED EXP Any one person': GLMedicalExpenses,
          //         'PERSONAL ADV INJURY': GLPersonalAdvInjury,
          //         'GENERAL AGGREGATE': GLAggregate,
          //         'PRODUCTS - COMPOP AGG': GLProductOps,

          //         'COMBINED SINGLE LIMIT': AUTOCombinedSingle,
          //         'BODILY INJURY Per Person': AUTOBiPerPerson,
          //         'BODILY INJURY Per Accident': AUTOBiPerAccident,
          //         'PROPERTY DAMAGE': AUTOPropertyDamage,
          //         'AL INS LETTER': AUTOInsurerLetter,
          //         'AL POLICY NMBER': AUTOPolicyNumber,
          //         'AL POLICY EFF': AUTOPolicyEffectiveDate,
          //         'AL POLICY EXP': AUTOPolicyExpirationDate,
          //         'AL ADDL': AUTOAddlInsured,
          //         'AL SUBR': AUTOSubrWvd,

          //         'EACH OCCURRENCE1': UMBPerOccurrence,
          //         AGGREGATE: UMBAggregate,
          //         'UL INS LETTER': UMBInsurerLetter,
          //         'UL POLICY NMBER': UMBPolicyNumber,
          //         'UL POLICY EFF': UMBPolicyEffectiveDate,
          //         'UL POLICY EXP': UMBPolicyExpirationDate,
          //         'UL ADDL': UMBAddlInsured,
          //         'UL SUBR': UMBSubrWvd,

          //         'EL EACH ACCIDENT': WCElEachAccident,
          //         'EL DISEASEEA EMPLOYEE': WCElDiseaseEachEmployee,
          //         'EL DISEASEPOLICY LIMIT': WCElDiseasePerPolicy,
          //         'WC INS LETTER': WCInsurerLetter,
          //         'WC POLICY NMBER': WCPolicyNumber,
          //         'WC POLICY EFF': WCPolicyEffectiveDate,
          //         'WC POLICY EXP': WCPolicyExpirationDate,
          //         'WC SUBR': WCSubrWvd
          //       } = scanResults;

          //       const scrubbedScanResults = {
          //         'PRODUCER NAME': prodName,
          //         'PRODUCER ADDRESS': prodAddress,
          //         'PRODUCER CITY': prodCity,
          //         'PRODUCER STATE': prodState,
          //         'PRODUCER ZIP': prodZip,
          //         Contact: contactName,
          //         Phone: contactPhone,
          //         Fax: contactFax,
          //         ADDRESS: contactEmail,

          //         'INSURER A': InsurerAName,
          //         'INSURER A NAIC': InsurerANAIC,
          //         'INSURER B': InsurerBName,
          //         'INSURER B NAIC': InsurerBNAIC,
          //         'INSURER C': InsurerCName,
          //         'INSURER C NAIC': InsurerCNAIC,
          //         'INSURER D': InsurerDName,
          //         'INSURER D NAIC': InsurerDNAIC,
          //         'INSURER E': InsurerEName,
          //         'INSURER E NAIC': InsurerENAIC,
          //         'INSURER F': InsurerFName,
          //         'INSURER F NAIC': InsurerFNAIC,

          //         'CL INS LETTER': GLInsurerLetter,
          //         'CL POLICY NMBER': GLPolicyNumber,
          //         'CL POLICY EFF': GLPolicyEffectiveDate,
          //         'CL POLICY EXP': GLPolicyExpirationDate,
          //         'CL ADDL': GLAddlInsured,
          //         'CL SUBR': GLSubrWvd,
          //         'EACH OCCURRENCE': GLPerOccurrence,
          //         'DAMAGE TO RENTED': GLDamageToRented,
          //         'MED EXP Any one person': GLMedicalExpenses,
          //         'PERSONAL ADV INJURY': GLPersonalAdvInjury,
          //         'GENERAL AGGREGATE': GLAggregate,
          //         'PRODUCTS - COMPOP AGG': GLProductOps,

          //         'COMBINED SINGLE LIMIT': AUTOCombinedSingle,
          //         'BODILY INJURY Per Person': AUTOBiPerPerson,
          //         'BODILY INJURY Per Accident': AUTOBiPerAccident,
          //         'PROPERTY DAMAGE': AUTOPropertyDamage,
          //         'AL INS LETTER': AUTOInsurerLetter,
          //         'AL POLICY NMBER': AUTOPolicyNumber,
          //         'AL POLICY EFF': AUTOPolicyEffectiveDate,
          //         'AL POLICY EXP': AUTOPolicyExpirationDate,
          //         'AL ADDL': AUTOAddlInsured,
          //         'AL SUBR': AUTOSubrWvd,

          //         'EACH OCCURRENCE1': UMBPerOccurrence,
          //         AGGREGATE: UMBAggregate,
          //         'UL INS LETTER': UMBInsurerLetter,
          //         'UL POLICY NMBER': UMBPolicyNumber,
          //         'UL POLICY EFF': UMBPolicyEffectiveDate,
          //         'UL POLICY EXP': UMBPolicyExpirationDate,
          //         'UL ADDL': UMBAddlInsured,
          //         'UL SUBR': UMBSubrWvd,

          //         'EL EACH ACCIDENT': WCElEachAccident,
          //         'EL DISEASEEA EMPLOYEE': WCElDiseaseEachEmployee,
          //         'EL DISEASEPOLICY LIMIT': WCElDiseasePerPolicy,
          //         'WC INS LETTER': WCInsurerLetter,
          //         'WC POLICY NMBER': WCPolicyNumber,
          //         'WC POLICY EFF': WCPolicyEffectiveDate,
          //         'WC POLICY EXP': WCPolicyExpirationDate,
          //         'WC SUBR': WCSubrWvd
          //       };

          //       Object.keys(scrubbedScanResults).forEach((field) => {
          //         if (!/(POLICY EFF|POLICY EXP)/g.test(field)) {
          //           scrubbedScanResults[
          //             field as keyof typeof scrubbedScanResults
          //           ] =
          //             scrubbedScanResults[
          //               field as keyof typeof scrubbedScanResults
          //             ].replace(/[^A-Za-z0-9-.,'&#:\s]/g, '') || null;
          //         } else {
          //           const DateTimeDate = DateTime.fromFormat(
          //             scrubbedScanResults[
          //               field as keyof typeof scrubbedScanResults
          //             ],
          //             'MM/dd/yyyy'
          //           );
          //           scrubbedScanResults[
          //             field as keyof typeof scrubbedScanResults
          //           ] = DateTimeDate.isValid ? DateTimeDate.toJSDate() : null;
          //         }
          //       });

          //       const LetterToScanInsurerNAICMap = new Map<string, string>([
          //         ['A', 'INSURER A NAIC'],
          //         ['B', 'INSURER B NAIC'],
          //         ['C', 'INSURER C NAIC'],
          //         ['D', 'INSURER D NAIC'],
          //         ['E', 'INSURER E NAIC'],
          //         ['F', 'INSURER F NAIC']
          //       ]);

          //       Array.from(this.Insurers.entries()).forEach((InsurerArray) => {
          //         const Insurer = InsurerArray[1];
          //         const { letter } = Insurer;

          //         const scanInsurerNAIC =
          //           LetterToScanInsurerNAICMap.get(letter);

          //         if (!scanInsurerNAIC) {
          //           throw this.ToastService.addErrorToast(
          //             'Error',
          //             'Missing scanned COI Insurer Letter.',
          //             { returnError: true }
          //           );
          //         }

          //         const scannedNAIC =
          //           scrubbedScanResults[
          //             scanInsurerNAIC as keyof typeof scrubbedScanResults
          //           ];
          //         Object.assign(Insurer, { naic: scannedNAIC });

          //         if (scannedNAIC) {
          //           this.performLookup(letter, Insurer).subscribe({
          //             next: () => {
          //               this.InsuranceCertificateFormGroup.controls[
          //                 'InsurancePolicies'
          //               ].controls.forEach((InsurancePolicyFormGroup) => {
          //                 const { value: policyType } =
          //                   InsurancePolicyFormGroup.controls['policyType'];
          //                 if (
          //                   (policyType === 'GL' &&
          //                     scrubbedScanResults['CL INS LETTER'] ===
          //                       letter) ||
          //                   (policyType === 'AUTO' &&
          //                     scrubbedScanResults['AL INS LETTER'] ===
          //                       letter) ||
          //                   (policyType === 'UMB' &&
          //                     scrubbedScanResults['UL INS LETTER'] ===
          //                       letter) ||
          //                   (policyType === 'WC' &&
          //                     scrubbedScanResults['WC INS LETTER'] === letter)
          //                 ) {
          //                   InsurancePolicyFormGroup.patchValue({
          //                     naic: scannedNAIC
          //                   });
          //                   this.setPolicyInsurer(
          //                     InsurancePolicyFormGroup,
          //                     letter
          //                   );
          //                   // If we don't patch this separately, it will result in an infinite loop calling the setPolicyInsurer function.
          //                   InsurancePolicyFormGroup.controls[
          //                     'insurerLetter'
          //                   ].patchValue(letter);
          //                 }
          //               });
          //             }
          //           });
          //         }
          //       });

          //       this.InsuranceCertificateFormGroup.patchValue({
          //         prodName,
          //         prodAddress,
          //         prodCity,
          //         prodState,
          //         prodZip,
          //         contactName,
          //         contactPhone,
          //         contactFax,
          //         contactEmail
          //       });

          //       this.InsuranceCertificateFormGroup.controls[
          //         'InsurancePolicies'
          //       ].controls.forEach((InsurancePolicyFormGroup) => {
          //         const { value: policyType } =
          //           InsurancePolicyFormGroup.controls['policyType'];

          //         if (policyType === 'GL') {
          //           InsurancePolicyFormGroup.patchValue({
          //             policyNumber: scrubbedScanResults['CL POLICY NMBER'],
          //             effectiveDate: scrubbedScanResults['CL POLICY EFF'],
          //             expirationDate: scrubbedScanResults['CL POLICY EXP'],
          //             addlInsured: ['Y', 'X', 'A'].includes(
          //               scrubbedScanResults['CL ADDL']
          //             ),
          //             subrWvd: ['Y', 'X', 'A'].includes(
          //               scrubbedScanResults['CL SUBR']
          //             ),
          //             perOccurence: scrubbedScanResults['EACH OCCURRENCE'],
          //             damageToRented: scrubbedScanResults['DAMAGE TO RENTED'],
          //             medicalExpenses:
          //               scrubbedScanResults['MED EXP Any one person'],
          //             personalAdvInjury:
          //               scrubbedScanResults['PERSONAL ADV INJURY'],
          //             aggregate: scrubbedScanResults['GENERAL AGGREGATE'],
          //             productOps: scrubbedScanResults['PRODUCTS - COMPOP AGG']
          //           });
          //         } else if (policyType === 'AUTO') {
          //           InsurancePolicyFormGroup.patchValue({
          //             combinedSingle:
          //               scrubbedScanResults['COMBINED SINGLE LIMIT'],
          //             biPerPerson:
          //               scrubbedScanResults['BODILY INJURY Per Person'],
          //             biPerAccident:
          //               scrubbedScanResults['BODILY INJURY Per Accident'],
          //             propertyDamage: scrubbedScanResults['PROPERTY DAMAGE'],
          //             policyNumber: scrubbedScanResults['AL POLICY NMBER'],
          //             effectiveDate: scrubbedScanResults['AL POLICY EFF'],
          //             expirationDate: scrubbedScanResults['AL POLICY EXP'],
          //             addlInsured: ['Y', 'X', 'A'].includes(
          //               scrubbedScanResults['AL ADDL']
          //             ),
          //             subrWvd: ['Y', 'X', 'A'].includes(
          //               scrubbedScanResults['AL SUBR']
          //             )
          //           });
          //         } else if (policyType === 'UMB') {
          //           InsurancePolicyFormGroup.patchValue({
          //             perOccurence: scrubbedScanResults['EACH OCCURRENCE1'],
          //             aggregate: scrubbedScanResults['AGGREGATE'],
          //             policyNumber: scrubbedScanResults['UL POLICY NMBER'],
          //             effectiveDate: scrubbedScanResults['UL POLICY EFF'],
          //             expirationDate: scrubbedScanResults['UL POLICY EXP'],
          //             addlInsured: ['Y', 'X', 'A'].includes(
          //               scrubbedScanResults['UL ADDL']
          //             ),
          //             subrWvd: ['Y', 'X', 'A'].includes(
          //               scrubbedScanResults['UL SUBR']
          //             )
          //           });
          //         } else if (policyType === 'WC') {
          //           InsurancePolicyFormGroup.patchValue({
          //             elEachAccident: scrubbedScanResults['EL EACH ACCIDENT'],
          //             elDiseaseEachEmployee:
          //               scrubbedScanResults['EL DISEASEEA EMPLOYEE'],
          //             elDiseasePerPolicy:
          //               scrubbedScanResults['EL DISEASEPOLICY LIMIT'],
          //             policyNumber: scrubbedScanResults['WC POLICY NMBER'],
          //             effectiveDate: scrubbedScanResults['WC POLICY EFF'],
          //             expirationDate: scrubbedScanResults['WC POLICY EXP'],
          //             subrWvd: ['Y', 'X', 'A'].includes(
          //               scrubbedScanResults['WC SUBR']
          //             )
          //           });
          //         } else if (policyType === 'PL') {
          //           // Doesn't appear to scan Professional Liability Policy.
          //         } else {
          //           throw this.ToastService.addErrorToast(
          //             'Error',
          //             'Invalid Policy Type.',
          //             { returnError: true }
          //           );
          //         }
          //       });
          //     },
          //     error: () => {
          //       mask.destroy();
          //       throw this.ToastService.addErrorToast(
          //         'Error',
          //         'An error occurred.',
          //         { returnError: true }
          //       );
          //     }
          //   });
          // }
        } else {
          this.SupportingFiles.push(CompanyVendorFile);
          this.cdr.markForCheck();
        }
      }
    });
  }

  setPolicyInsurer(
    InsurancePolicyFormGroup: InsurancePolicyForm,
    insurerLetter: string
  ) {
    // If the VIVE Admin is setting the insurer to a blank value (for some reason) don't run this code.
    if (insurerLetter) {
      const Insurer = this.AvailableInsurersMappedByLetter.get(insurerLetter);

      if (Insurer) {
        if (!this.InsurersMappedToInsurancePolicyFormGroups.has(Insurer)) {
          this.InsurersMappedToInsurancePolicyFormGroups.set(Insurer, []);
        }

        this.InsurersMappedToInsurancePolicyFormGroups.get(Insurer)!.push(
          InsurancePolicyFormGroup
        );

        const { name, naic, amBestNumber, amBestRating, sizeCategory } =
          Insurer;

        InsurancePolicyFormGroup.patchValue({
          insurer: name || undefined,
          naic: naic ? String(naic) : null,
          aMBestCompanyNumber: amBestNumber ? amBestNumber.toString() : null,
          amBestRating: amBestRating ? (amBestRating as AmBestRating) : null,
          financialSize: (sizeCategory || '').trim()
            ? (sizeCategory as FinancialSize)
            : null
        });
      }
    } else {
      // May not be necessary.
      // Should remove the insurer if one is set from InsurersMappedToInsurancePolicyFormGroups.

      InsurancePolicyFormGroup.patchValue({
        insurer: undefined,
        insurerLetter: undefined,
        naic: null,
        aMBestCompanyNumber: null,
        amBestRating: null,
        financialSize: null
      });
    }
  }

  removeFile(
    { File, index }: { File: FileModel | License; index: number },
    docType: 'COI' | 'Supporting File'
  ) {
    if (docType === 'COI') {
      this.COIFiles.splice(index, 1);
    } else {
      this.SupportingFiles.splice(index, 1);
    }
  }

  copyAgentContactInfo() {
    const { firstName, lastName, email, phone1 } = this.InsuranceAgent;
    this.InsuranceCertificateFormGroup.patchValue({
      contactName: `${firstName} ${lastName}`,
      contactEmail: email || undefined,
      contactPhone: phone1 || undefined
    });
  }

  cancel() {
    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', modalDialogClass: 'confirm-modal' }
    );
    Object.assign(componentInstance, {
      displayText: 'Are you sure you want to cancel?'
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          this.router.navigate([
            'admin',
            'run-vive',
            'client-vendor',
            this.CompanyVendorId,
            'client',
            this.CompanyId,
            'vendor',
            this.VendorId,
            'compliance-type',
            this.CompanyComplianceTypeId,
            'action',
            this.action,
            'insurance-review'
          ]);
        }
      }
    });
  }

  save() {
    this.InsuranceCertificateFormGroup.controls['CompanyVendorFiles'].clear();
    [...this.COIFiles, ...this.SupportingFiles].forEach((CompanyVendorFile) => {
      const CompanyVendorFileFormGroup = new CompanyVendorFileForm();
      CompanyVendorFileFormGroup.patchValue(CompanyVendorFile);
      this.InsuranceCertificateFormGroup.controls['CompanyVendorFiles'].push(
        CompanyVendorFileFormGroup
      );
    });

    const { controls } = this.InsuranceCertificateFormGroup;
    const InsuranceCertificate =
      this.InsuranceCertificateFormGroup.getRawValue();

    const { status } = InsuranceCertificate;
    const checkValidation = status
      ? !new Set<InsuranceCertificateStatus>(['Draft']).has(status)
      : true;

    if (controls['InsurancePolicies'].length === 0 && checkValidation) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save Insurance Certificate with no policies added.',
        { returnError: true }
      );
    }

    const errors = this.UtilityService.generateValidationErrorsObject(
      this.InsuranceCertificateFormGroup
    );

    if (errors && checkValidation) {
      const { InsurancePolicies, CompanyVendorFiles } = errors;

      if (CompanyVendorFiles) {
        const { needSelection } = CompanyVendorFiles;
        if (needSelection) {
          throw this.ToastService.addErrorToast(
            'Error',
            'An Insurance Certificate file must be uploaded.',
            { returnError: true }
          );
        }

        // Handle other errors individually.
        // throw this.ToastService.addErrorToast('Error', 'There is an error with an Insurance Certificate file.', { returnError: true });
      }

      if (InsurancePolicies) {
        const isPolicyExpirationDateLessThanCurrentDate = Object.keys(
          InsurancePolicies
        ).find((key) => {
          const value = InsurancePolicies[key];
          const { dateLessThanCurrentDate } = value;
          return dateLessThanCurrentDate;
        });

        if (isPolicyExpirationDateLessThanCurrentDate) {
          throw this.ToastService.addErrorToast(
            'Error',
            "Policy expiration date cannot be less than today's date.",
            { returnError: true }
          );
        }

        throw this.ToastService.addErrorToast(
          'Error',
          'There is an error with an Insurance Policy field below.',
          { returnError: true }
        );
      }

      if (!CompanyVendorFiles && !InsurancePolicies) {
        throw this.ToastService.addErrorToast(
          'Error',
          'There is an error with a field below.',
          { returnError: true }
        );
      }
    }

    const { CompanyVendorFiles } = InsuranceCertificate;

    if (!CompanyVendorFiles) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor files.',
        { returnError: true }
      );
    }

    const SaveInsuranceCertificate = defer(() => {
      return this.InsuranceService.saveInsuranceCertificate(
        InsuranceCertificate
      ).pipe(
        concatMap((SavedInsuranceCertificate) => {
          const { id } = SavedInsuranceCertificate;
          this.InsuranceCertificateFormGroup.patchValue({ id });
          this.ToastService.addSuccessToast(
            'Success',
            'Insurance Certificate successfully saved.'
          );
          this.router.navigate([
            'admin',
            'run-vive',
            'client-vendor',
            this.CompanyVendorId,
            'client',
            this.CompanyId,
            'vendor',
            this.VendorId,
            'compliance-type',
            this.CompanyComplianceTypeId,
            'action',
            this.action,
            'insurance-review'
          ]);

          return of(true);
        })
      );
    });

    (() => {
      if (status && status === 'Archived') {
        const { componentInstance, result } = this.ModalService.open(
          ConfirmModalComponent,
          { backdrop: 'static', modalDialogClass: 'confirm-modal' }
        );
        Object.assign(componentInstance, {
          displayText:
            'Are you sure you want to archive this Insurance Certificate?'
        });

        return from(result).pipe(
          concatMap((confirm) => {
            if (confirm) {
              return of(true);
            }
            return of(false);
          })
        );
      }

      return of(true);
    })().subscribe({
      next: (value) => {
        if (value) {
          (() => {
            if (this.ReplaceCOIId) {
              return this.InsuranceService.saveInsuranceCertificate({
                id: this.ReplaceCOIId,
                CompanyId: this.CompanyId,
                VendorId: this.VendorId,
                InsuranceAgentId: this.InsuranceAgentId,
                status: 'Replaced'
              });
            }

            return of<InsuranceCertificate | null>(null);
          })().subscribe({
            next: () => {
              SaveInsuranceCertificate.subscribe();
            }
          });
        }
      }
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe({
      next: (paramMap) => {
        const CompanyVendorId = Number(paramMap.get('CompanyVendorId'));
        const CompanyId = Number(paramMap.get('CompanyId'));
        const VendorId = Number(paramMap.get('VendorId'));
        const CompanyComplianceTypeId = Number(
          paramMap.get('CompanyComplianceTypeId')
        );
        const InsuranceAgentId = Number(paramMap.get('InsuranceAgentId'));
        const InsuranceCertificateId = Number(
          paramMap.get('InsuranceCertificateId')
        );
        const ReplaceCOIId = Number(paramMap.get('ReplaceCOIId'));
        const action = paramMap.get('action');

        if (!CompanyVendorId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client/Vendor Id.',
            { returnError: true }
          );
        }

        if (!CompanyId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
            returnError: true
          });
        }

        if (!VendorId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
            returnError: true
          });
        }

        if (!CompanyComplianceTypeId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Compliance Type Id.',
            { returnError: true }
          );
        }

        if (!InsuranceAgentId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Insurance Agent Id.',
            { returnError: true }
          );
        }

        if (!action) {
          throw this.ToastService.addErrorToast('Error', 'Missing action.', {
            returnError: true
          });
        }

        if (ReplaceCOIId) {
          this.ReplaceCOIId = Number(ReplaceCOIId);
        }

        Object.assign(this, {
          CompanyVendorId,
          CompanyId,
          VendorId,
          CompanyComplianceTypeId,
          InsuranceAgentId,
          action
        });

        this.InsuranceCertificateFormGroup.patchValue({
          CompanyVendorId,
          VendorId,
          CompanyId,
          InsuranceAgentId,
          status: 'Reviewing'
        });

        this.updateAvailableStatuses();

        // Insert the previously uploaded files into COI File tab
        const routerState =
          this.router.getCurrentNavigation()?.extras?.state ||
          window.history.state;

        if (routerState && routerState['uploadedFiles']) {
          const uploadedFiles = routerState['uploadedFiles'] as FileModel[];
          uploadedFiles.forEach((file) => {
            const companyVendorFile = new CompanyVendorFile({
              CompanyVendorId,
              VendorId,
              CompanyId,
              docType: 'COI',
              File: file
            });
            this.COIFiles.push(companyVendorFile);
          });
          this.cdr.markForCheck();
        }

        forkJoin([
          this.SubNationalDivisionService.getSubNationalDivisions(),
          this.ComplianceService.getTierLevelForVendor(
            this.CompanyId,
            this.VendorId
          )
        ]).subscribe({
          next: ([SubNationalDivisions, riskLevel]) => {
            this.SubNationalDivisions.push(...SubNationalDivisions);
            this.vendorRiskLevel = riskLevel;

            const InsuranceAgentQuery = { id: InsuranceAgentId };

            if (this.ReplaceCOIId || InsuranceCertificateId) {
              Object.assign(InsuranceAgentQuery, {
                $or: [{ isArchived: true }, { isArchived: false }],
                InsuranceCertificate: {
                  id: this.ReplaceCOIId || InsuranceCertificateId,
                  status: {
                    $in: [
                      'Received',
                      'Reviewing',
                      'Accepted',
                      'Rejected',
                      'Archived',
                      'Replaced',
                      'Draft',
                      'Created'
                    ]
                  },
                  CompanyVendorFile: {
                    docType: { $in: ['COI', 'Supporting File'] },
                    File: {}
                  },
                  InsurancePolicy: {
                    InsurancePolicySite: {
                      Site: { attributes: ['id'] }
                    }
                  }
                }
              });
            }

            this.CompanyVendorService.getCompanyVendor(
              this.UtilityService.stringifyObjectsInQueryObject({
                id: CompanyVendorId,
                Company: {
                  attributes: [
                    'id',
                    'legalName',
                    'allowAdditionalInsuredEndorsements',
                    'country'
                  ],
                  ComplianceSetting: {
                    attributes: [
                      'id',
                      'CompanyId',
                      'CompanyComplianceTypeId',
                      'riskLevel',
                      'disallowWCExemptions',
                      'generalLiability_isRequired',
                      'autoLiability_isRequired',
                      'umbrellaLiability_isRequired',
                      'workersCompensation_isRequired',
                      'errorsAndOmissions_isRequired'
                    ],
                    riskLevel,
                    CompanyComplianceTypeId: {
                      $eq: {
                        $col: 'CompanyVendor.CompanyComplianceTypeId'
                      }
                    },
                    CompanyComplianceType: {
                      attributes: ['id', 'type']
                    }
                  }
                },
                Vendor: {
                  attributes: ['id', 'legalName'],
                  aliasedAttributes: [
                    'hasProfessionalTrade',
                    'hasProfessionalOrNonProfessionalTrade'
                  ],
                  InsuranceAgent: InsuranceAgentQuery
                }
              })
            ).subscribe({
              next: (CompanyVendor) => {
                if (!CompanyVendor) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Client/Vendor information.',
                    { returnError: true }
                  );
                }

                const { country, Vendor, Company } = CompanyVendor;

                if (!country) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing country information.',
                    { returnError: true }
                  );
                }

                if (!Vendor) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Vendor information.',
                    { returnError: true }
                  );
                }

                if (!Company) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Client information.',
                    { returnError: true }
                  );
                }

                this.country = country;

                if (this.country === 'CA') {
                  this.AvailableInsurancePoliciesMap.delete('WC');
                }

                this.AvailableInsurancePolicies = Array.from(
                  this.AvailableInsurancePoliciesMap.entries()
                );

                const { InsuranceAgents } = Vendor;

                if (
                  !Array.isArray(InsuranceAgents) ||
                  InsuranceAgents.length === 0
                ) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Insurance Agent information.',
                    { returnError: true }
                  );
                }

                if (InsuranceAgents.length !== 1) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'There was an error loading Insurance Agent information.',
                    { returnError: true }
                  );
                }

                const { ComplianceSettings } = Company;

                if (
                  !Array.isArray(ComplianceSettings) ||
                  ComplianceSettings.length === 0
                ) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Client compliance settings.',
                    { returnError: true }
                  );
                }

                if (ComplianceSettings.length !== 1) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'There was an error loading Client compliance setting information.',
                    { returnError: true }
                  );
                }

                Object.assign(this, {
                  InsuranceAgent: InsuranceAgents[0],
                  Vendor,
                  Company,
                  ComplianceSetting: ComplianceSettings[0]
                });

                const {
                  firstName: agentFirstName,
                  lastName: agentLastName,
                  email: agentEmail,
                  InsuranceCertificates
                } = this.InsuranceAgent;

                if (!agentFirstName) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Agent is missing first name.',
                    { returnError: true }
                  );
                }

                if (!agentLastName) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Agent is missing last name.',
                    { returnError: true }
                  );
                }

                if (!agentEmail) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Agent is missing email address.',
                    { returnError: true }
                  );
                }

                this.InsuranceCertificateFormGroup.patchValue({
                  agentFirstName,
                  agentLastName,
                  agentEmail
                });

                if (
                  this.ReplaceCOIId &&
                  (!Array.isArray(InsuranceCertificates) ||
                    InsuranceCertificates.length !== 1)
                ) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'There was an error replacing the COI. Could not find COI to replace.',
                    { returnError: true }
                  );
                }

                if (Array.isArray(InsuranceCertificates)) {
                  if (InsuranceCertificates.length !== 1) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'There was an error loading Insurance Certificate data.',
                      { returnError: true }
                    );
                  }

                  const InsuranceCertificate = InsuranceCertificates[0];

                  this.InsuranceCertificateFormGroup.patchValue(
                    InsuranceCertificate
                  );

                  this.updateAvailableStatuses();

                  if (this.ReplaceCOIId) {
                    this.InsuranceCertificateFormGroup.patchValue({
                      id: undefined,
                      status: 'Reviewing'
                    });
                  }

                  const { InsurancePolicies, CompanyVendorFiles } =
                    InsuranceCertificate;

                  if (!Array.isArray(InsurancePolicies)) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'Missing Insurance Policies from Insurance Certificate.',
                      { returnError: true }
                    );
                  }

                  forkJoin(
                    InsurancePolicies.map((policy) => {
                      const {
                        policyType,

                        insurer: name,
                        insurerLetter: letter,
                        amBestRating,
                        aMBestCompanyNumber: amBestNumber,
                        naic,
                        financialSize: sizeCategory,

                        InsurancePolicySites
                      } = policy;

                      if (!policyType) {
                        throw this.ToastService.addErrorToast(
                          'Error',
                          'Insurance Policy is missing policy type.',
                          { returnError: true }
                        );
                      }

                      return (() => {
                        if (letter) {
                          const InsurerObject = this.Insurers.get(letter);

                          if (InsurerObject) {
                            Object.assign(InsurerObject, {
                              name,
                              letter,
                              amBestRating,
                              amBestNumber,
                              naic,
                              sizeCategory
                            });

                            if (name) {
                              this.insurerNameChanged(InsurerObject, name);
                            }
                          }
                        }

                        return of<void | null>(null);
                      })().pipe(
                        concatMap(() => {
                          const InsurancePolicyFormGroup =
                            new InsurancePolicyForm();
                          const InsurancePolicySitesFormArray =
                            new FormArray<InsurancePolicySiteForm>([]);

                          if (InsurancePolicySites) {
                            InsurancePolicySites.forEach(
                              (InsurancePolicySite) => {
                                const { Site } = InsurancePolicySite;

                                if (!Site) {
                                  throw this.ToastService.addErrorToast(
                                    'Error',
                                    'Insurance Policy Site is missing Site information.',
                                    { returnError: true }
                                  );
                                }

                                const { id: SiteId } = Site;

                                if (!SiteId) {
                                  throw this.ToastService.addErrorToast(
                                    'Error',
                                    'Site is missing id.',
                                    { returnError: true }
                                  );
                                }

                                const InsurancePolicySiteFormGroup =
                                  new InsurancePolicySiteForm();
                                InsurancePolicySiteFormGroup.patchValue({
                                  SiteId
                                });
                                const SiteFormGroup = new SiteForm();
                                SiteFormGroup.patchValue(Site);
                                InsurancePolicySiteFormGroup.setControl(
                                  'Site',
                                  SiteFormGroup
                                );
                                InsurancePolicySitesFormArray.push(
                                  InsurancePolicySiteFormGroup
                                );
                              }
                            );

                            InsurancePolicyFormGroup.setControl(
                              'InsurancePolicySites',
                              InsurancePolicySitesFormArray
                            );
                          }

                          InsurancePolicyFormGroup.patchValue(policy);

                          if (this.ReplaceCOIId) {
                            InsurancePolicyFormGroup.patchValue({
                              id: undefined,
                              status: 'Active',
                              effectiveDate: null,
                              expirationDate: null
                            });
                          }

                          this.InsuranceCertificateFormGroup.controls[
                            'InsurancePolicies'
                          ].push(InsurancePolicyFormGroup);

                          if (policyType === 'AUTO') {
                            const { value: isCaSplitLimit } =
                              InsurancePolicyFormGroup.controls[
                                'isCaSplitLimit'
                              ];
                            this.splitLimitPolicyChange(!!isCaSplitLimit);
                          }

                          this.removeAvailableInsurancePolicy(policyType);
                          this.setPolicyInsurer(
                            InsurancePolicyFormGroup,
                            letter || ''
                          );

                          return of(null);
                        })
                      );
                    })
                  ).subscribe({
                    next: () => {
                      this.setPolicyFormControlRequirements();
                    }
                  });

                  if (Array.isArray(CompanyVendorFiles) && !this.ReplaceCOIId) {
                    while (CompanyVendorFiles.length > 0) {
                      const CompanyVendorFile = CompanyVendorFiles.shift();

                      if (!CompanyVendorFile) {
                        throw this.ToastService.addErrorToast(
                          'Error',
                          'There was an error processing the Client/Vendor file.',
                          { returnError: true }
                        );
                      }

                      const { docType, File } = CompanyVendorFile;

                      if (!docType) {
                        throw this.ToastService.addErrorToast(
                          'Error',
                          'Missing document type from COI.',
                          { returnError: true }
                        );
                      }

                      if (!File) {
                        throw this.ToastService.addErrorToast(
                          'Error',
                          'File is missing from COI.',
                          { returnError: true }
                        );
                      }

                      if (!['COI', 'Supporting File'].includes(docType)) {
                        throw this.ToastService.addErrorToast(
                          'Error',
                          'Invalid document type on COI.',
                          { returnError: true }
                        );
                      }

                      if (docType === 'COI') {
                        this.COIFiles.push(CompanyVendorFile);
                      }

                      if (docType === 'Supporting File') {
                        this.SupportingFiles.push(CompanyVendorFile);
                      }
                    }
                  }
                }
                this.cdr.markForCheck();
              }
            });
          }
        });
      }
    });
  }
}
